import React, { useMemo, useState } from "react";
import {
    SendingDomainMethod,
    SendingDomainStatus,
} from "@common/types/domains/emailSending";
import { useSendingDomains } from "@api/sendingDomain.api";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Button } from "@design-system/Button/Button";
import { Section } from "@design-system/Section/Section";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useWorkspaceSendingEmails } from "@hooks/useWorkspaceSendingEmails";
import { useWorkspaceSendingOwner } from "@hooks/useWorkspaceSendingOwner";
import { AddSendingEmailModal } from "./AddSendingEmailModal";
import { ConnectGmailButton } from "./ConnectGmailButton";
import { SendingEmailsView } from "./SendingEmailsView";
import {
    SUGGESTED_SENDING_EMAIL_ROW_ID,
    type SendingEmailRow,
} from "./sendingEmailsViewConfig";
import { useSuggestedCustomSender } from "./useSuggestedCustomSender";

export const SendingEmailsSection: React.FC = () => {
    const owner = useWorkspaceSendingOwner();
    const { data: domains } = useSendingDomains(owner);
    const { data: emails, isLoading } = useWorkspaceSendingEmails();
    const [search, setSearch] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const suggestion = useSuggestedCustomSender();

    const hasVerifiedSmtpDnsDomain = (domains ?? []).some(
        (domain) =>
            domain.method !== SendingDomainMethod.gmail &&
            domain.status === SendingDomainStatus.verified,
    );

    const tableData = useMemo<SendingEmailRow[] | null>(() => {
        if (isLoading) {
            return null;
        }
        const rows: SendingEmailRow[] = [...(emails ?? [])];
        if (suggestion?.kind === "gmail") {
            rows.unshift({
                id: SUGGESTED_SENDING_EMAIL_ROW_ID,
                email: suggestion.email,
                displayName: null,
                domain: suggestion.domain,
                isDefault: false,
                isVerified: false,
                isSuggested: true,
            });
        }
        if (!search) {
            return rows;
        }
        const normalizedSearch = search.toLowerCase();
        return rows.filter((email) =>
            [email.email, email.displayName ?? "", email.domain].some((value) =>
                value.toLowerCase().includes(normalizedSearch),
            ),
        );
    }, [emails, isLoading, search, suggestion]);

    const showHeaderGmailConnect = suggestion?.kind !== "gmail";

    return (
        <Section
            heading="Custom sender emails"
            subheading="Add from addresses on a sending domain. One can be marked as the workspace default."
        >
            <TablePageHeader search={search} onSearchChange={setSearch}>
                {showHeaderGmailConnect && <ConnectGmailButton />}
                <Tooltip
                    content={
                        hasVerifiedSmtpDnsDomain
                            ? undefined
                            : "Set up SMTP/DNS for your domain before adding an email"
                    }
                >
                    <Button
                        variant="primary"
                        size="md"
                        disabled={!hasVerifiedSmtpDnsDomain}
                        onClick={() => setShowAddModal(true)}
                    >
                        Add email
                    </Button>
                </Tooltip>
            </TablePageHeader>
            <SendingEmailsView data={tableData} />
            <AddSendingEmailModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
            />
        </Section>
    );
};
